import * as React from 'react';
import type { SecondFactorVerificationFailedAction } from '@Application/Security/Authentications/Actions/secondFactorVerificationFailed';
import VerifySecondFactor from '@Application/Security/Authentications/Commands/VerifySecondFactor';
import verifySecondFactor from '@Application/Security/Authentications/verifySecondFactor';
import AuthenticationNotifications from '@UI/Pages/Authentication/Shared/AuthenticationNotifications';
import RecoveryCodeFormData from '@UI/Pages/Authentication/SecondFactor/FormData/RecoveryCodeFormData';
import SecondFactorFormData from '@UI/Pages/Authentication/SecondFactor/FormData/SecondFactorFormData';
import recoveryCodeModeSignal, {
	setRecoveryCodeMode,
} from '@UI/Pages/Authentication/SecondFactor/Signals/recoveryCodeModeSignal';
import { css } from '@emotion/react';
import resolvePostLoginDestination from '@UI/Routing/resolvePostLoginDestination';
import { useLocation, useNavigate } from 'react-router-dom';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch } from '@verclam/synapse';
import { Button, Card, Form, InputText } from '@verclam/ui-kit';
import { useToast } from '@verclam/toast-v2';

const buttonWrapperStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	margin: 0.75rem 1.25rem 0 1.25rem;
`;

// Ten codes are issued at enrollment; warning below a third of that leaves enough uses to react
// without nagging on every successful verification.
const RECOVERY_CODES_WARNING_THRESHOLD = 3;

/**
 * Reloading mid-challenge loses `sessionStatus` (the `mfa_challenge` cookie is `httpOnly`), which
 * sends the visitor back to `/login` — the route guard handles that, not this component.
 */
const SecondFactorPage: React.FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const toast = useToast();
	const isRecoveryCodeMode = useSignalValue(recoveryCodeModeSignal);

	const [codeFormData, setCodeFormData] = React.useState(() => SecondFactorFormData.createEmpty());
	const [recoveryFormData, setRecoveryFormData] = React.useState(() => RecoveryCodeFormData.createEmpty());
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	React.useEffect(() => {
		// A fresh mount always starts on the temporary-code field, never on a leftover mode from a
		// previous visit to this screen.
		setRecoveryCodeMode(false);
	}, []);

	const handleCodeChange = React.useCallback((value: string) => {
		setCodeFormData((current) => current.setCode(value));
	}, []);

	const handleRecoveryCodeChange = React.useCallback((value: string) => {
		setRecoveryFormData((current) => current.setRecoveryCode(value));
	}, []);

	const handleToggleMode = React.useCallback(() => {
		setRecoveryCodeMode(!recoveryCodeModeSignal.peek());
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (isRecoveryCodeMode) {
			if (!recoveryFormData.isFormDataValid()) {
				setRecoveryFormData(recoveryFormData.showErrors());
				return;
			}
		} else if (!codeFormData.isFormDataValid()) {
			setCodeFormData(codeFormData.showErrors());
			return;
		}

		setIsSubmitting(true);

		const command = isRecoveryCodeMode
			? new VerifySecondFactor({ recoveryCode: recoveryFormData.recoveryCode })
			: new VerifySecondFactor({ code: codeFormData.code });

		dispatch(verifySecondFactor(command))
			.then((action) => {
				if (action.type !== 'SECOND_FACTOR_VERIFIED') {
					return;
				}

				const remainingRecoveryCodes = action.payload.remainingRecoveryCodes;

				if (remainingRecoveryCodes === 0) {
					toast.current?.show({
						severity: 'error',
						summary: 'Codes de récupération',
						detail: 'Aucun code de récupération n’est disponible. Contactez votre administrateur pour réinitialiser votre second facteur.',
						sticky: true,
					});
				} else if (remainingRecoveryCodes <= RECOVERY_CODES_WARNING_THRESHOLD) {
					toast.current?.show({
						severity: 'warn',
						summary: 'Codes de récupération',
						detail:
							remainingRecoveryCodes === 1
								? 'Il vous reste 1 code de récupération.'
								: `Il vous reste ${remainingRecoveryCodes} codes de récupération.`,
						life: 6000,
					});
				}

				navigate(resolvePostLoginDestination(location.state), { replace: true });
			})
			.catch((action: SecondFactorVerificationFailedAction) => {
				const eventNames = 'SECOND_FACTOR_VERIFICATION_FAILED';
				const serverFieldErrors = action.payload?.fieldErrors ?? [];

				// The server names no field — the challenge has only one — so the field is marked with a
				// short wording, and the server's own explanation stays in the toast.
				const fieldErrorsOn = (field: string) =>
					serverFieldErrors.length > 0
						? serverFieldErrors
						: [{ field, message: 'Code incorrect.' }];

				if (isRecoveryCodeMode) {
					updateValidationFromExternalSource({
						action: {
							type: action.type,
							payload: { fieldErrors: fieldErrorsOn('recoveryCode') },
						},
						eventNames,
						formDataUpdater: setRecoveryFormData,
					});
					return;
				}

				updateValidationFromExternalSource({
					action: { type: action.type, payload: { fieldErrors: fieldErrorsOn('code') } },
					eventNames,
					formDataUpdater: setCodeFormData,
				});
			})
			.finally(() => setIsSubmitting(false));
	}, [dispatch, isRecoveryCodeMode, codeFormData, recoveryFormData, navigate, location, toast]);

	return (
		<React.Fragment>
			<Card title="Vérification en deux étapes" width="100%" maxWidth="26rem">
				<Form onSubmit={handleSubmit}>
					{isRecoveryCodeMode ? (
						<InputText
							name="recoveryCode"
							label="Code de récupération"
							autoFocus
							required
							value={recoveryFormData.recoveryCode}
							onChange={handleRecoveryCodeChange}
							errorMessage={recoveryFormData.getValidationMessage('recoveryCode')}
							isFormFieldValid={recoveryFormData.isValid('recoveryCode')}
						/>
					) : (
						<InputText
							name="code"
							label="Code de vérification"
							keyfilter="pint"
							maxLength={6}
							autoFocus
							required
							value={codeFormData.code}
							onChange={handleCodeChange}
							errorMessage={codeFormData.getValidationMessage('code')}
							isFormFieldValid={codeFormData.isValid('code')}
						/>
					)}
					<div css={buttonWrapperStyle}>
						<Button type="submit" label="Valider" loading={isSubmitting} />
						<Button
							type="button"
							label={
								isRecoveryCodeMode
									? 'Utiliser mon application d’authentification'
									: 'Utiliser un code de récupération'
							}
							text
							onClick={handleToggleMode}
						/>
					</div>
				</Form>
			</Card>
			<AuthenticationNotifications />
		</React.Fragment>
	);
};

export default SecondFactorPage;
